import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { GroupPickerField } from '@lumapps/group-pickers/components/GroupPickerField';
import { UseGroupPickerFieldStateOptions } from '@lumapps/group-pickers/hooks/useGroupPickerFieldState';
import { Group } from '@lumapps/groups/types';
import { doesCurrentInstanceAllowPublicContent } from '@lumapps/instance/ducks/selectors';
import { mdiAccountPlus } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';

import { CONTENTS } from '../../keys';

import './index.scss';

export type ContentAudienceTargetingProps = {
    defaultVisibleByIds: string[];
    defaultHighlightedForIds: string[];
    restrictToFeedsIds?: string[];
    isVisibleByMissing?: boolean;
    isDisabled?: boolean;
    onVisibleChange: (visibleByGroups: (string | Group)[]) => void;
    onHighlightedChange: (highlightedForGroups: (string | Group)[]) => void;
    groupAll?: Group;
    groupPublic?: Group;
};

const CLASSNAME = 'content-audience-targeting';

export const ContentAudienceTargeting = ({
    defaultVisibleByIds,
    defaultHighlightedForIds,
    restrictToFeedsIds,
    groupAll,
    groupPublic,
    isVisibleByMissing,
    isDisabled = false,
    onVisibleChange,
    onHighlightedChange,
}: ContentAudienceTargetingProps) => {
    const { translateKey } = useTranslate();
    const isPublicContentAuthorized = useSelector(doesCurrentInstanceAllowPublicContent);
    const { block, element } = useClassnames(CLASSNAME);

    // Need internal state to have great perf in legacy context when the user pick a group
    const [visibleBy, setVisibleBy] = React.useState<(Group | string)[]>(defaultVisibleByIds ?? []);
    const [highlightedFor, setHighlightedFor] = React.useState<(Group | string)[]>(defaultHighlightedForIds ?? []);

    const containsAllOrPublic = React.useCallback(
        (ids: string[]) => {
            return (groupAll && ids.includes(groupAll?.id)) || (groupPublic && ids.includes(groupPublic.id));
        },
        [groupAll, groupPublic],
    );

    const isAllInRestriction = React.useMemo(() => {
        if (groupAll && restrictToFeedsIds) {
            return restrictToFeedsIds.includes(groupAll.id);
        }

        return false;
    }, [restrictToFeedsIds, groupAll]);

    const isPublicInRestriction = React.useMemo(() => {
        if (groupPublic && restrictToFeedsIds) {
            return restrictToFeedsIds.includes(groupPublic.id);
        }

        return false;
    }, [restrictToFeedsIds, groupPublic]);

    /** Build custom params */
    const customVisibleByParams = React.useMemo<UseGroupPickerFieldStateOptions['searchParams']>(
        () => ({
            restrictToFeeds: isAllInRestriction || isPublicInRestriction ? [] : restrictToFeedsIds,
            excludeSegments: false,
        }),
        [restrictToFeedsIds, isAllInRestriction, isPublicInRestriction],
    );
    const customHighlightedForParams = React.useMemo<UseGroupPickerFieldStateOptions['searchParams']>(() => {
        const visibleByIds = visibleBy.map((gr) => (typeof gr === 'string' ? gr : gr.id));
        return {
            restrictToFeeds: containsAllOrPublic(visibleByIds) ? [] : visibleByIds,
            excludeSegments: false,
        };
    }, [visibleBy, containsAllOrPublic]);

    const shouldDisplayDefaultGroups = React.useMemo(() => {
        const ids = visibleBy.map((gr) => (typeof gr === 'string' ? gr : gr.id));
        return groupAll && ids.includes(groupAll?.id);
    }, [groupAll, visibleBy]);

    const onHighlightedForChange = (groups: (string | Group)[] | undefined) => {
        setHighlightedFor(groups ?? []);
        onHighlightedChange(groups ?? []);
    };

    const onVisibleByChange = (groups: Group[] | undefined) => {
        const groupIds = groups?.map((gr) => gr.id);

        setVisibleBy(groups ?? []);
        onVisibleChange(groups ?? []);

        // Can only highlight for groups that are in the visible by
        if (groupIds && !containsAllOrPublic(groupIds)) {
            // check if some highlighted for groups should be removed
            const cleanedGroups = highlightedFor.filter((group) =>
                groupIds.includes(typeof group === 'string' ? group : group.id),
            );

            onHighlightedForChange(cleanedGroups);
        }
    };

    return (
        <div className={block()}>
            <GroupPickerField
                selectionType="multiple"
                label={translateKey(CONTENTS.SETTINGS_VISIBLE_BY)}
                onChange={onVisibleByChange}
                value={visibleBy}
                searchParams={customVisibleByParams}
                shouldDisplayPublicGroup={
                    isPublicContentAuthorized &&
                    (!restrictToFeedsIds || (groupPublic && (isPublicInRestriction || isAllInRestriction)))
                }
                shouldDisplayAllGroup={
                    !restrictToFeedsIds || (groupAll && (isAllInRestriction || isPublicInRestriction))
                }
                className={element('visible-by')}
                selectTextFieldProps={{
                    helper: translateKey(CONTENTS.SETTINGS_VISIBLE_BY_HELP),
                    isDisabled,
                    icon: mdiAccountPlus,
                    scope: `${CLASSNAME}-visible-by`,
                    error: translateKey(CONTENTS.FEED_REQUIRED),
                    hasError: isVisibleByMissing && visibleBy.length === 0,
                }}
            />
            <GroupPickerField
                selectionType="multiple"
                label={translateKey(CONTENTS.SETTINGS_HIGHLIGHTED_FEEDS)}
                onChange={onHighlightedForChange}
                value={highlightedFor}
                searchParams={customHighlightedForParams}
                shouldDisplayPublicGroup={false}
                shouldDisplayAllGroup={shouldDisplayDefaultGroups}
                shouldDisplaySegments
                className={element('highlighted-for')}
                selectTextFieldProps={{
                    helper: translateKey(CONTENTS.SETTINGS_HIGHLIGHTED_FEEDS_HELP),
                    scope: `${CLASSNAME}-highlighted-for`,
                    icon: mdiAccountPlus,
                    isDisabled: visibleBy.length === 0 || isDisabled,
                }}
            />
        </div>
    );
};
